import { Page } from "@components-common/layouts";
import { FormProvider } from "../../../../components-common/forms/provider";
import { useEffect, useState } from "react";
import { Loader } from "taltech-styleguide";
import { PresetsTableSelection } from "./PresetsTableSelection";
import { useLoading } from "../../../../components-common/_shared/loading/use-loading.hook";
import { application } from "../../../../_runtime/bootstrap";
import { Toolbar } from "@components-common/toolbar";
import * as Yup from "yup";
import {
  FormBase,
  InputDescription,
  InputName,
} from "@components-common/forms";
import { useNavigate } from "react-router-dom";
import { useFormikContext } from "formik";
import { useUserContext } from "../../../../_runtime/iam/session.context";
import { roleMapper } from "../../../../_runtime/iam/session.provider";
import { isEmpty } from "ramda";
import { getPresetCreationSchema } from "../schema";

/**
 * Functional Component: PageContent
 *
 * This component renders the content of the page, including the form, toolbar, and table for selecting preset items.
 *
 * Props:
 * @param {Object} props - The properties passed to the component.
 * @param {Object} props.form - The form configuration object.
 * @param {Object} props.presetItems - The preset items to be displayed in the table.
 * @param {Array} props.columns - The columns configuration for the table.
 * @param {Object} props.toolbar - The toolbar configuration object.
 * @param {Function} props.setLoading - Function to set the loading state.
 * @param {string} props.createPresetItemsSource - The source URL for creating preset items.
 * @param {string} props.navUrl - The URL to navigate to after form submission.
 *
 * Returns:
 * @returns {JSX.Element} A JSX element containing the page content.
 */
const PageContent = ({
  form,
  presetItems,
  columns,
  toolbar,
  setLoading,
  createPresetItemsSource,
  navUrl,
}: any) => {
  const nav = useNavigate();
  const formik = useFormikContext();
  const [selected, setSelected] = useState<string[]>([]);
  const { userId, userRole } = useUserContext();

  const submitForm = async () => {
    formik.submitForm();
    const isValid = await formik.validateForm().then((res) => res);
    if (!isEmpty(isValid)) return;
    const values = {
      ...(formik.values || {}),
      sourceId: userId,
      sourceType: userRole === roleMapper.Student ? "USER" : "SYSTEM",
    };
    setLoading(true);
    application
      .getHttpClient()
      .post(`/buildingms/presets`, values)
      .then((res: any) => {
        if (selected.length > 0) {
          selected.map((itemId) => {
            application
              .getHttpClient()
              .post(createPresetItemsSource, {
                presetId: res.body.id,
                [createPresetItemsSource === "/buildingms/presets/spaces"
                  ? "spaceFunctionId"
                  : "buildingParameterId"]: itemId,
              })
              .then(() => {
                nav(navUrl);
              });
          });
        } else nav(navUrl);
      });
  };
  return (
    <>
      <Toolbar
        {...{
          ...toolbar,
          left: [
            { kind: "create_new", onAction: async () => await submitForm() },
          ],
          right: ["back"],
        }}
      />
      <InputName elementClass="required" />
      <InputDescription />
      <PresetsTableSelection
        tableItems={presetItems.items}
        columns={columns}
        selected={selected}
        setSelected={setSelected}
        hasEdit={true}
      />
    </>
  );
};

/**
 * Functional Component: PresetCreatePage
 *
 * This component renders the page for creating a new preset. It includes a form, toolbar, and a table for selecting preset items.
 *
 * Props:
 * @param {Object} props - The properties passed to the component.
 * @param {string} props.title - The title of the page.
 * @param {Object} props.form - The form configuration object.
 * @param {Object} props.toolbar - The toolbar configuration object.
 * @param {Array} props.columns - The columns configuration for the table.
 * @param {string} props.getPresetItemsSource - The source URL for fetching preset items.
 * @param {string} props.createPresetItemsSource - The source URL for creating preset items.
 * @param {string} props.navUrl - The URL to navigate to after form submission.
 *
 * Returns:
 * @returns {JSX.Element} A JSX element containing the preset creation page.
 */
export const PresetCreatePage = ({
  title,
  form,
  toolbar,
  columns,
  getPresetItemsSource,
  createPresetItemsSource,
  navUrl,
}: any) => {
  const [loading, setLoading] = useLoading(true);
  const [data, setData] = useState<any>([]);
  form.schema = getPresetCreationSchema();

  useEffect(() => {
    application
      .getHttpClient()
      .get(getPresetItemsSource)
      .then((res: any) => {
        setData(res.body);
        setLoading(false);
      });
  }, []);

  return (
    <Page title={title}>
      {loading && <Loader overlay />}
      {!loading && (
        <FormProvider>
          <FormBase {...form}>
            <PageContent
              form={form}
              presetItems={data}
              columns={columns}
              toolbar={toolbar}
              setLoading={setLoading}
              createPresetItemsSource={createPresetItemsSource}
              navUrl={navUrl}
            />
          </FormBase>
        </FormProvider>
      )}
    </Page>
  );
};
